import {
    calculateMinAreaAngle,
    calculateOrientationCorrection,
    loadImage,
    resizeCanvas,
    rotateCanvas,
    trimCanvas,
} from './canvasUtils';

/** 1.5" bleed at 72 DPI added around black-background print files. */
export const BLACK_BORDER_PX = 108;
const DPI = 72;
/** Relative aspect-ratio difference still treated as "same framing" (resize rounding, 1px trim padding). */
const ASPECT_TOLERANCE = 0.02;

export type PrintBgColor = 'white' | 'black' | 'transparent';

export const bgColorFor = (color: string | null | undefined): PrintBgColor => {
    const c = (color || '').toLowerCase();
    if (c.includes('black')) return 'black';
    if (c.includes('white')) return 'white';
    return 'transparent';
};

/** Longest side in inches from the SKU suffix (SH-ID-30 → 30), 16 when absent. */
export const skuTargetInches = (sku: string | null | undefined): number => {
    const m = (sku || '').match(/(\d{2})$/);
    return m ? parseInt(m[1], 10) : 16;
};

export const cloneCanvas = (src: HTMLCanvasElement): HTMLCanvasElement => {
    const c = document.createElement('canvas');
    c.width = src.width;
    c.height = src.height;
    c.getContext('2d')!.drawImage(src, 0, 0);
    return c;
};

/** Decode through <img> like the editor always did, so pixels match what the reviewer saw. */
export const blobToCanvas = async (blob: Blob): Promise<HTMLCanvasElement> => {
    const url = URL.createObjectURL(blob);
    try {
        const img = await loadImage(url);
        const c = document.createElement('canvas');
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        const ctx = c.getContext('2d')!;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0);
        return c;
    } finally {
        URL.revokeObjectURL(url);
    }
};

export const canvasToBlob = (canvas: HTMLCanvasElement, type: 'image/png' | 'image/jpeg', quality = 1) =>
    new Promise<Blob>((resolve, reject) =>
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Failed to encode image'))), type, quality)
    );

/**
 * Width of the solid black bleed frame of a black-background draft: 108 when the server
 * followed the print-ready convention, 0 when the object runs to the edge. Only the outer
 * 100px are sampled — JPEG blocks can ring a few pixels into the frame next to the object.
 */
export const detectBlackBorder = (draft: HTMLCanvasElement): number => {
    const w = draft.width;
    const h = draft.height;
    if (w <= BLACK_BORDER_PX * 2 + 2 || h <= BLACK_BORDER_PX * 2 + 2) return 0;
    const depth = BLACK_BORDER_PX - 8;
    const ctx = draft.getContext('2d')!;
    const strips = [
        ctx.getImageData(0, 0, w, depth),
        ctx.getImageData(0, h - depth, w, depth),
        ctx.getImageData(0, 0, depth, h),
        ctx.getImageData(w - depth, 0, depth, h),
    ];
    let dark = 0;
    let total = 0;
    for (const s of strips) {
        const d = s.data;
        for (let i = 0; i < d.length; i += 4) {
            total += 1;
            if (d[i] <= 24 && d[i + 1] <= 24 && d[i + 2] <= 24) dark += 1;
        }
    }
    return dark / total >= 0.995 ? BLACK_BORDER_PX : 0;
};

export interface IOutputGeometry {
    bg: PrintBgColor;
    /** Bleed frame to add on save (black only). */
    border: number;
    /** Longest side of the object inside the frame — the edited object is resized to exactly this. */
    targetLongPx: number;
    /** Object area of the draft (draft minus frame); null without a draft. */
    inner: { w: number; h: number } | null;
    source: 'draft' | 'sku';
}

/**
 * Output size for an edited file. The draft the reviewer approved is the reference: the
 * edit keeps its print size and frame. Without a draft, fall back to the SKU size.
 */
export const resolveOutputGeometry = (draft: HTMLCanvasElement | null, bg: PrintBgColor, sku: string): IOutputGeometry => {
    if (draft) {
        const border = bg === 'black' ? detectBlackBorder(draft) : 0;
        const inner = { w: draft.width - border * 2, h: draft.height - border * 2 };
        return { bg, border, targetLongPx: Math.max(inner.w, inner.h), inner, source: 'draft' };
    }
    return {
        bg,
        border: bg === 'black' ? BLACK_BORDER_PX : 0,
        targetLongPx: skuTargetInches(sku) * DPI,
        inner: null,
        source: 'sku',
    };
};

const aspectDiff = (w: number, h: number, ref: { w: number; h: number }) =>
    Math.abs(w / h - ref.w / ref.h) / (ref.w / ref.h);

export interface IAlignedObject {
    canvas: HTMLCanvasElement;
    /** Rotation applied to match the draft (0 when the framing already matched). */
    angle: number;
    /** true — framing matches the draft; false — it doesn't; null — no draft to compare with. */
    matched: boolean | null;
    aspectDiff: number | null;
}

/**
 * Turn the server's background-removed object into the editing master so that it frames
 * the object exactly like the draft does. The draft may have been auto-rotated (min-area
 * rotation + orientation restore, as in the old client pipeline); in that case the same
 * rotation is replayed ONCE on the full-resolution object. Crops are drawImage-based,
 * so no pixel is resampled unless a rotation is really needed.
 */
export const alignObjectToDraft = (source: HTMLCanvasElement, inner: { w: number; h: number } | null): IAlignedObject => {
    const trimmed = cloneCanvas(source);
    trimCanvas(trimmed);
    if (!inner || inner.w <= 0 || inner.h <= 0) return { canvas: trimmed, angle: 0, matched: null, aspectDiff: null };
    const plainDiff = aspectDiff(trimmed.width, trimmed.height, inner);
    if (plainDiff <= ASPECT_TOLERANCE) return { canvas: trimmed, angle: 0, matched: true, aspectDiff: plainDiff };

    const angle = calculateMinAreaAngle(source);
    if (Math.abs(angle) > 0.5) {
        const total = angle + calculateOrientationCorrection(angle);
        const rotated = cloneCanvas(source);
        rotateCanvas(rotated, total);
        trimCanvas(rotated);
        const rotatedDiff = aspectDiff(rotated.width, rotated.height, inner);
        if (rotatedDiff <= ASPECT_TOLERANCE) return { canvas: rotated, angle: total, matched: true, aspectDiff: rotatedDiff };
    }
    return { canvas: trimmed, angle: 0, matched: false, aspectDiff: plainDiff };
};

/**
 * Cap the editing master at 2x the output size (same as the old pipeline): enough headroom
 * for crops, while 30 undo snapshots of a 40+ MP original would exhaust memory.
 * No sharpening here — the single downscale on save sharpens once.
 */
export const capEditingMaster = async (canvas: HTMLCanvasElement, targetLongPx: number) => {
    if (Math.max(canvas.width, canvas.height) > targetLongPx * 2) {
        await resizeCanvas(canvas, (targetLongPx * 2) / DPI, 'transparent', false);
    }
};

/** Inches ↔ the `targetSize` argument of resizeCanvas (which multiplies by 72). */
export const pxToResizeInches = (px: number) => px / DPI;
